// Templates and generated HTML media (docs/09 §B) are authored on a fixed
// 1920×1080 canvas. Dropping that document into a small CSS-sized iframe clips
// it — the iframe's viewport becomes the little box, not the 1920×1080 the slide
// laid itself out for, so anything past the box's edge is cut off. This primitive
// renders the iframe at its TRUE authoring size and scales the whole frame down
// (transform: scale) to fit the container width, so the full slide is always
// visible, letterboxed to 16:9. One place owns this so every preview surface
// (media grid, live preview, gallery, assistant confirmation card) shares it.

import { useLayoutEffect, useRef, useState, type HTMLAttributeReferrerPolicy } from 'react';

export const AUTHOR_WIDTH = 1920;
export const AUTHOR_HEIGHT = 1080;

/** Scale factor that fits a 1920-wide authored frame into `containerWidth`. */
export function scaleForWidth(containerWidth: number): number {
  return containerWidth / AUTHOR_WIDTH;
}

export function ScaledFrame({
  srcDoc,
  title,
  sandbox = 'allow-scripts',
  referrerPolicy,
  className,
}: {
  srcDoc: string;
  title: string;
  sandbox?: string;
  referrerPolicy?: HTMLAttributeReferrerPolicy;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  // useLayoutEffect so the first paint already carries the correct scale (no
  // one-frame flash of an unscaled 1920px frame). ResizeObserver keeps it exact
  // as the container reflows (sidebar toggles, window resize, grid columns).
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => setScale(scaleForWidth(host.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return; // jsdom / older runtimes
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={hostRef} className={`scaled-frame${className ? ` ${className}` : ''}`}>
      <iframe
        title={title}
        sandbox={sandbox}
        referrerPolicy={referrerPolicy}
        srcDoc={srcDoc}
        width={AUTHOR_WIDTH}
        height={AUTHOR_HEIGHT}
        style={{ transform: `scale(${scale})` }}
      />
    </div>
  );
}
